import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { 
  Search, Book, Users, Zap, MessageCircle, 
  Lightbulb, Vote, Bell, User, Shield, 
  LifeBuoy, ChevronRight, HelpCircle
} from 'lucide-react';

const HelpSection = ({ icon: Icon, title, children }) => (
  <div className="mb-16">
    <div className="flex items-center gap-4 mb-8">
      <div className="p-3 rounded-2xl bg-brand-indigo/10 text-brand-indigo">
        <Icon size={24} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {children}
    </div>
  </div>
);

const HelpCard = ({ title, description, steps }) => (
  <div className="p-8 rounded-[32px] border border-gray-100 bg-white hover:border-brand-indigo/30 transition-all group">
    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-brand-indigo transition-colors">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed mb-4">{description}</p>
    {steps && (
      <ol className="space-y-2">
        {steps.map((step, idx) => (
          <li key={idx} className="flex items-center gap-3 text-sm text-gray-500">
            <span className="w-5 h-5 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-400">
              {idx + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    )}
  </div>
);

const HelpCenter = () => {
  return (
    <StaticContentPage title="Help Center">
      <div className="max-w-3xl mb-16">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to the Chooziq Help Center.
        </p>
        <p className="text-gray-600 leading-relaxed">
          Chooziq is a realtime interaction and collaboration platform designed for communities, live events, workshops, classrooms, webinars, conferences, and collaborative engagement experiences. This Help Center provides guidance on using the platform, managing communities, participating in live sessions, and resolving common issues.
        </p>
      </div>

      <HelpSection icon={Book} title="Getting Started">
        <HelpCard 
          title="What is Chooziq?" 
          description="A live interaction platform where users can create communities, host live sessions, engage in realtime Q&A, collaborate through brainstorming, and participate in live polls."
        />
        <HelpCard 
          title="Creating an Account" 
          description="To start using Chooziq, follow these steps:"
          steps={['Visit Chooziq', 'Select Sign Up', 'Enter account info', 'Verify email address', 'Complete profile setup']}
        />
      </HelpSection>

      <HelpSection icon={Users} title="Communities">
        <HelpCard 
          title="Joining a Community" 
          description="Join public communities from discovery page. Some may require approval, invitation links, or organization access."
        />
        <HelpCard 
          title="Creating a Community" 
          description="Host discussions, events, workshops, and learning environments. Manage members, permissions, and visibility."
        />
      </HelpSection>

      <HelpSection icon={Zap} title="Live Rooms and Sessions">
        <HelpCard 
          title="What is a Live Room?" 
          description="An interactive space for realtime engagement including Q&A, brainstorming, polls, voting, and collaborative activities."
        />
        <HelpCard 
          title="Joining a Live Session" 
          description="Open the session page, select Join Live Room, and use the available interaction tools."
          steps={['Open session page', 'Select Join Live Room', 'Participate with tools']}
        />
      </HelpSection>

      <HelpSection icon={MessageCircle} title="Interactions">
        <HelpCard 
          title="Live Q&A" 
          description="Submit questions publicly or anonymously, upvote questions to prioritize them, and see pinned questions from moderators."
        />
        <HelpCard 
          title="Brainstorming Boards" 
          description="Collaborate in realtime by sharing ideas, feedback, and concepts. Create cards and vote on contributions."
        />
        <HelpCard 
          title="Polls and Voting" 
          description="Participate in multiple-choice polls, ratings, sentiment checks, quizzes, and decision-based voting."
        />
        <HelpCard 
          title="Notifications" 
          description="Stay informed about live sessions, replies, mentions, community activity, and moderator actions."
        />
      </HelpSection>

      <HelpSection icon={Shield} title="Account and Security">
        <HelpCard 
          title="Resetting Your Password" 
          description="If you forget your password, follow these steps:"
          steps={['Select Forgot Password', 'Enter registered email', 'Follow reset instructions']}
        />
        <HelpCard 
          title="Account Security" 
          description="Use strong passwords, avoid sharing credentials, and sign out from shared devices."
        />
      </HelpSection>

      <div className="bg-gray-50 p-10 rounded-[48px] border border-gray-100 mb-16">
        <div className="flex flex-col md:flex-row gap-8 items-center text-center md:text-left">
          <div className="p-4 rounded-3xl bg-white shadow-sm">
            <LifeBuoy className="text-brand-indigo" size={40} />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Technical Support</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Experiencing technical issues? Contact us with your device type, browser information, and a description of the issue.
            </p>
            <a href="mailto:support@chooziq.com" className="font-bold text-brand-indigo hover:underline flex items-center justify-center md:justify-start gap-2">
              support@chooziq.com <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </div>

      <div className="p-10 bg-gray-900 rounded-[48px] text-white text-center">
        <HelpCircle className="text-brand-indigo mx-auto mb-6" size={48} />
        <h3 className="text-2xl font-bold mb-4">Frequently Asked Questions</h3>
        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
          Can't find what you're looking for? Check our FAQ for quick answers to common questions.
        </p>
        <a href="/faq" className="inline-flex items-center gap-2 bg-brand-indigo hover:bg-brand-indigo/90 text-white px-8 py-4 rounded-2xl font-bold transition-all">
          Visit FAQ Page
        </a>
      </div>
    </StaticContentPage>
  );
};

export default HelpCenter;
